// Server data through TanStack Query: what to fetch, how often, and what to do with the clock when it arrives.
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { api } from "@/api/client";
import type { InitData, PlayerChar, PlayerVillage, PresenceVillage, Village, WorldDetails, WorldsOverview } from "@/api/types";
import { useClock } from "./clock";
import { useSession } from "./session";

export const POLL = {
  village: 15_000, // the active city
  worlds: 300_000,
  init: 600_000,
};

export const keys = {
  init: ["init"] as const,
  worlds: (wid: number) => ["worlds", wid] as const,
  village: (wid: number, vid: number) => ["village", wid, vid] as const,
  villagesOfWorld: (wid: number) => ["village", wid] as const,
};

export function useInit(enabled = true) {
  const setWorldNames = useSession((s) => s.setWorldNames);
  const q = useQuery({
    queryKey: keys.init,
    queryFn: () => api.init(),
    staleTime: POLL.init,
    enabled,
  });
  useEffect(() => {
    if (q.data?.config?.world_names) setWorldNames(q.data.config.world_names);
  }, [q.data, setWorldNames]);
  return q;
}

export function useWorldsOverview(wid: number | null, enabled = true) {
  return useQuery({
    queryKey: keys.worlds(wid ?? 0),
    queryFn: async () => (await api.portal.worlds({ wid: wid! })).worlds,
    staleTime: POLL.worlds,
    refetchInterval: POLL.worlds,
    enabled: enabled && !!wid,
  });
}

/** The city, polled while the tab is visible; each answer re-syncs the server clock. */
export function useVillage(
  wid: number | null,
  vid: number | null,
  opts: { poll?: number | false; enabled?: boolean } = {},
) {
  const sample = useClock((s) => s.sample);
  const poll = opts.poll === undefined ? POLL.village : opts.poll;
  const q = useQuery({
    queryKey: keys.village(wid ?? 0, vid ?? 0),
    queryFn: () => readVillage(wid!, vid!, sample),
    enabled: (opts.enabled ?? true) && !!wid && !!vid,
    staleTime: 5_000,
    refetchInterval: poll ? poll : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  } satisfies UseQueryOptions<Village, Error, Village, ReturnType<typeof keys.village>>);
  return q;
}

const readVillage = async (wid: number, vid: number, sample: (serverMs: number, sentAt?: number) => void) => {
  const sentAt = Date.now();
  const v = await api.village.get({ wid, vid });
  if (typeof v.server_time === "number") sample(v.server_time * 1000, sentAt);
  return v;
};

/** Read a city ahead of showing it (the swipe city-switch loads the next city while the clouds cover the screen): the
 *  same cache entry `useVillage` reads, so the screen shows it at once; a copy younger than 5 s is used as is. */
export function fetchVillage(qc: ReturnType<typeof useQueryClient>, wid: number, vid: number): Promise<Village> {
  return qc.fetchQuery({ queryKey: keys.village(wid, vid), queryFn: () => readVillage(wid, vid, useClock.getState().sample), staleTime: 5_000 });
}

/** Read a city unless the cache has a copy younger than `freshMs` (the city switcher's incoming times). */
export function fetchVillageFresh(qc: ReturnType<typeof useQueryClient>, wid: number, vid: number, freshMs: number): Promise<Village> {
  return qc.fetchQuery({ queryKey: keys.village(wid, vid), queryFn: () => readVillage(wid, vid, useClock.getState().sample), staleTime: freshMs });
}

/** The city a world-scoped screen (map, reports, mail, guild, leaderboards…) belongs to: the remembered one, else the
 *  first the game lists for this world (as Empire does), remembered from then on. Polls village/get like a city screen. */
export function useCurrentCity(wid: number, poll?: number | false) {
  const sessionVid = useSession((s) => s.vid);
  const setVillage = useSession((s) => s.setVillage);
  const init = useInit();
  const vid = useMemo(() => {
    if (sessionVid) return sessionVid;
    const ids = presenceVillages(init.data?.world, wid).map(presenceVillageId).filter((x): x is number => !!x);
    return ids[0] ?? null;
  }, [sessionVid, init.data, wid]);
  useEffect(() => {
    if (!sessionVid && vid) setVillage(vid);
  }, [sessionVid, vid, setVillage]);
  const q = useVillage(wid, vid, { poll });
  return { vid, v: q.data, q, setVillage };
}

/** After an action answered with { village_state }, put the fresh city into the cache. */
export function useVillageCache() {
  const qc = useQueryClient();
  return {
    put: (wid: number, vid: number, village: Village) => {
      if (typeof village.server_time === "number") useClock.getState().sample(village.server_time * 1000);
      qc.setQueryData(keys.village(wid, vid), village);
    },
    invalidate: (wid: number, vid?: number) =>
      qc.invalidateQueries({ queryKey: vid ? keys.village(wid, vid) : keys.villagesOfWorld(wid) }),
  };
}

// ------------------------------------------------------------------ reading the shapes

/** all_worlds_details may come as an object keyed by world id (normal) or an array. */
export function worldDetails(o: WorldsOverview | undefined, wid: number): WorldDetails | undefined {
  if (!o) return undefined;
  const d = o.all_worlds_details as Record<string, WorldDetails> | WorldDetails[];
  if (Array.isArray(d)) return d[wid];
  return d?.[String(wid)];
}

/** The player's cities in a world as the worlds overview lists them (shape loose: ids or objects). */
export function presenceVillages(o: WorldsOverview | undefined, wid: number): PresenceVillage[] {
  if (!o) return [];
  const v = o.villages_in as Record<string, PresenceVillage[]> | PresenceVillage[];
  if (Array.isArray(v)) return (v[wid] as unknown as PresenceVillage[]) ?? [];
  return v?.[String(wid)] ?? [];
}

const pick = (o: Record<string, unknown>, ...names: string[]): unknown => {
  for (const n of names) if (o[n] !== undefined && o[n] !== null) return o[n];
  return undefined;
};

/** The id of a city in the presence list, whatever the field is called. */
export function presenceVillageId(v: PresenceVillage): number | null {
  if (typeof v === "number") return v;
  if (v && typeof v === "object") {
    const id = pick(v, "Id", "id", "vid", "VillageId", "village_id");
    return typeof id === "number" ? id : typeof id === "string" && /^\d+$/.test(id) ? Number(id) : null;
  }
  return null;
}

export function presenceVillageName(v: PresenceVillage): string | null {
  if (!v || typeof v !== "object") return null;
  const n = pick(v, "Name", "name", "CityName", "city_name");
  return typeof n === "string" ? n : null;
}

/** The player's own character block in village/get (keyed by user id; also tolerates a bare target_char). */
export function playerChar(v: Village | undefined, uid: number | null): PlayerChar | undefined {
  if (!v?.player_details) return undefined;
  const byId = uid ? (v.player_details[String(uid)] as { target_char?: PlayerChar } | undefined) : undefined;
  if (byId?.target_char) return byId.target_char;
  for (const val of Object.values(v.player_details)) {
    const tc = (val as { target_char?: PlayerChar } | undefined)?.target_char;
    if (tc && Array.isArray(tc.villages)) return tc;
  }
  return undefined;
}

/** All the player's cities in this world, from any village/get of the world. */
export function ownVillages(v: Village | undefined, uid: number | null): PlayerVillage[] {
  return playerChar(v, uid)?.villages ?? [];
}

/** The guild name/tag block, wherever the server put it. */
export function guildBrief(v: Village | undefined, uid: number | null): { name: string; tag: string } | undefined {
  const tc = playerChar(v, uid);
  if (tc?.guild_info) return tc.guild_info;
  const stray = (v?.player_details?.target_char as { guild_info?: { name: string; tag: string } } | undefined)?.guild_info;
  if (stray) return stray;
  const gi = v?.guild_info as { name?: string; tag?: string } | undefined;
  if (gi?.name && gi?.tag) return { name: gi.name, tag: gi.tag };
  return undefined;
}

export type { InitData };
